import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "./ui.jsx";
import { toast } from "./Toast.jsx";
import { getClient as apiGetClient } from "../api.js";

/**
 * Etiquetas de correo para los clientes seleccionados (Lissette 2026-08-07:
 * "un modulo para imprimir label de clientes... para cuando tengamos que
 * enviar postales o carta").
 *
 * Formato Avery 5160: 30 etiquetas por hoja carta, 3 columnas x 10 filas.
 * Es el estándar de etiquetas de dirección en EE.UU. y se consigue en
 * cualquier papelería.
 *
 * NO guarda nada en la base de datos. El nombre personalizado se elige al
 * imprimir entre varios estilos, y se puede corregir uno a uno para ese envío
 * concreto — que es exactamente lo que pidió: "tener opciones a la hora de
 * imprimir, escoger".
 */

// ── Formatos de hoja ────────────────────────────────────────────────────────
//
// Todos sobre papel carta (8.5 x 11 pulgadas) y con las medidas oficiales de
// Avery. Comprobación de que cuadran:
//
//   5160 — 0.1875 + (3 x 2.625) + (2 x 0.125) + 0.1875 = 8.5  |  0.5 + 10x1   + 0.5 = 11
//   5161 — 0.15625 + (2 x 4)    + 0.1875 + 0.15625     = 8.5  |  0.5 + 10x1   + 0.5 = 11
//   5162 — igual de ancho que el 5161                        |  0.83 + 7x1.33 + 0.86 = 11
//   5163 — igual de ancho que el 5161                        |  0.5 + 5x2     + 0.5 = 11
//
// Las medidas van en PULGADAS y no en píxeles a propósito: al imprimir, una
// pulgada es una pulgada. En píxeles, cualquier cambio de zoom o de DPI
// descuadraría la hoja entera y las direcciones saldrían fuera del adhesivo.
const FORMATOS = [
  {
    id: "5160", nombre: "Avery 5160", columnas: 3, filas: 10,
    ancho: "2.625in", alto: "1in", huecoColumna: "0.125in",
    margenSuperior: "0.5in", margenLateral: "0.1875in", fuente: "10.5pt",
    es: "30 por hoja — direcciones (el más común)",
    en: "30 per sheet — address labels (most common)",
  },
  {
    id: "5161", nombre: "Avery 5161", columnas: 2, filas: 10,
    ancho: "4in", alto: "1in", huecoColumna: "0.1875in",
    margenSuperior: "0.5in", margenLateral: "0.15625in", fuente: "11pt",
    es: "20 por hoja — más anchas",
    en: "20 per sheet — wider",
  },
  {
    id: "5162", nombre: "Avery 5162", columnas: 2, filas: 7,
    ancho: "4in", alto: "1.33in", huecoColumna: "0.1875in",
    margenSuperior: "0.83in", margenLateral: "0.15625in", fuente: "12pt",
    es: "14 por hoja — letra grande",
    en: "14 per sheet — large print",
  },
  {
    id: "5163", nombre: "Avery 5163", columnas: 2, filas: 5,
    ancho: "4in", alto: "2in", huecoColumna: "0.1875in",
    margenSuperior: "0.5in", margenLateral: "0.15625in", fuente: "13pt",
    es: "10 por hoja — envíos y paquetes",
    en: "10 per sheet — shipping",
  },
];
const porHoja = (f) => f.columnas * f.filas;

const ESTILOS = [
  { id: "nombre_apellido", es: "Nombre y apellido", en: "First and last name" },
  { id: "solo_nombre", es: "Solo el nombre", en: "First name only" },
  { id: "familia", es: "Familia", en: "Family" },
  { id: "titulo", es: "Sr. / Sra.", en: "Mr. / Mrs." },
  { id: "pareja", es: "Titular y cónyuge", en: "Client and spouse" },
];

/**
 * Cómo se escribe el nombre según el estilo elegido.
 *
 * Los casos con respaldo (`||`) importan: un cliente sin cónyuge en estilo
 * "pareja", o sin sexo en estilo "Sr./Sra.", NO puede quedarse sin nombre en
 * la etiqueta — un sobre sin destinatario no llega. Cae al nombre completo.
 */
export function nombreParaEtiqueta(c, estilo, es) {
  const nombre = (c.firstName || "").trim();
  const apellido = (c.lastName || "").trim();
  const completo = [nombre, apellido].filter(Boolean).join(" ");

  switch (estilo) {
    case "solo_nombre":
      return nombre || completo;

    case "familia":
      if (!apellido) return completo;
      return es ? `Familia ${apellido}` : `The ${apellido} Family`;

    case "titulo": {
      // Usa el campo Sexo publicado hoy. Sin ese dato no se inventa un
      // tratamiento: dirigirse a alguien como "Sr." cuando es "Sra." es peor
      // que no poner tratamiento.
      const g = (c.gender || "").toLowerCase();
      const trato = g === "male" ? (es ? "Sr." : "Mr.")
        : g === "female" ? (es ? "Sra." : "Mrs.")
        : "";
      return trato ? `${trato} ${completo}`.trim() : completo;
    }

    case "pareja": {
      const conyuge = ((c.spouse && c.spouse.firstName) || "").trim();
      if (!conyuge) return completo;
      const union = es ? "y" : "and";
      // "José y María Pérez" — un solo apellido cuando lo comparten, que es
      // lo normal en un envío al hogar.
      return apellido
        ? `${nombre} ${union} ${conyuge} ${apellido}`
        : `${nombre} ${union} ${conyuge}`;
    }

    case "nombre_apellido":
    default:
      return completo;
  }
}

/** Las dos líneas de dirección, ya formateadas. */
export function lineasDireccion(c) {
  const calle = (c.address || c.addressStreet || "").trim();
  const ciudad = (c.city || c.addressCity || "").trim();
  const estado = (c.state || c.addressState || "").trim();
  const cp = (c.zipCode || c.addressZip || "").trim();
  const ultima = [[ciudad, estado].filter(Boolean).join(", "), cp].filter(Boolean).join(" ");
  return { calle, ultima };
}

/** Una etiqueta necesita, como mínimo, calle y ciudad. */
export function tieneDireccion(c) {
  const { calle, ultima } = lineasDireccion(c);
  return !!calle && !!ultima;
}

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Trae las fichas completas de los clientes seleccionados.
 *
 * Hace falta porque la LISTA de clientes se carga a propósito sin dirección
 * (ver el comentario de exportClientsFull en api.js: "the regular list is
 * deliberately light — no address/income/marital/spouse detail/notes"). Sin
 * esto, el módulo decía "sin dirección" para todo el mundo, y al abrir la
 * ficha la dirección sí estaba.
 *
 * Se piden UNO A UNO en vez de usar exportClientsFull: ese endpoint devuelve
 * todos los clientes del ámbito CON sus notas — pedir el historial de la
 * cartera entera para imprimir unos sobres sería traer muchísimo más PHI del
 * necesario. Aquí solo viajan las fichas de quienes se van a imprimir.
 *
 * De 6 en 6: en paralelo pero sin abrir cien conexiones de golpe contra la
 * API. Un fallo suelto no tumba el resto — ese cliente queda marcado y los
 * demás se imprimen.
 */
async function hidratarClientes(base, onProgreso) {
  const LOTE = 6;
  const salida = [];
  for (let i = 0; i < base.length; i += LOTE) {
    const lote = base.slice(i, i + LOTE);
    const res = await Promise.all(lote.map(async (c) => {
      try {
        const full = await apiGetClient(c.id);
        // Fusionar y no reemplazar: la ficha completa manda, pero si algún
        // campo solo existiera en la fila de la lista no se pierde.
        return { ...c, ...full };
      } catch {
        return { ...c, __noCargo: true };
      }
    }));
    salida.push(...res);
    onProgreso(salida.length);
  }
  return salida;
}

export function MailingLabelsModal({ clients = [], onClose, lang }) {
  const es = lang === "es";
  const [estilo, setEstilo] = useState("nombre_apellido");
  // Correcciones a mano, por id de cliente. Solo para esta impresión: no se
  // guardan en la ficha.
  const [ajustes, setAjustes] = useState({});
  // Primera casilla de la hoja. Sirve para reaprovechar una hoja a la que ya
  // le quitaste unas cuantas etiquetas, que es lo que pasa en la práctica.
  const [empezarEn, setEmpezarEn] = useState(1);
  const [formatoId, setFormatoId] = useState("5160");
  const formato = FORMATOS.find(f => f.id === formatoId) || FORMATOS[0];

  // Fichas completas. Arranca vacío y se llena al abrir: la lista de clientes
  // no trae direcciones (ver hidratarClientes).
  const esApi = typeof window !== "undefined" && !!window.__deskteAuth;
  const [fichas, setFichas] = useState(() => (esApi ? [] : clients));
  const [cargando, setCargando] = useState(esApi);
  const [progreso, setProgreso] = useState(0);

  useEffect(() => {
    if (!esApi) { setFichas(clients); setCargando(false); return; }
    let vivo = true;
    setCargando(true);
    setProgreso(0);
    hidratarClientes(clients, (n) => { if (vivo) setProgreso(n); })
      .then((res) => { if (vivo) { setFichas(res); setCargando(false); } })
      .catch(() => {
        if (!vivo) return;
        // Caída total (sin red): se sigue con lo que había en la lista, que al
        // menos deja ver los nombres y decir claramente qué falta.
        toast.error(es ? "No se pudieron cargar las direcciones." : "Couldn't load the addresses.");
        setFichas(clients);
        setCargando(false);
      });
    return () => { vivo = false; };
    // clients es una lista nueva en cada render del padre, así que se depende
    // de los ids y no del array: si no, esto se relanzaría en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.map(c => c.id).join(","), esApi]);

  const { conDireccion, sinDireccion } = useMemo(() => {
    const con = [], sin = [];
    for (const c of fichas) (tieneDireccion(c) ? con : sin).push(c);
    return { conDireccion: con, sinDireccion: sin };
  }, [fichas]);

  const nombreDe = (c) => (
    ajustes[c.id] !== undefined ? ajustes[c.id] : nombreParaEtiqueta(c, estilo, es)
  );

  const imprimir = () => {
    if (conDireccion.length === 0) {
      toast.error(es ? "Ninguno de los clientes seleccionados tiene dirección." : "None of the selected clients has an address.");
      return;
    }

    const F = formato;
    const N = porHoja(F);
    const saltar = Math.max(0, Math.min(N - 1, Number(empezarEn) - 1));

    // ── Los estilos van EN CADA ELEMENTO, no en una hoja <style> ────────────
    //
    // Las dos primeras versiones ponían la geometría en un <style> del
    // documento y el navegador la ignoró por completo al imprimir: sin
    // rejilla, sin negrita, todo pegado en una columna (2026-08-08, "cuando le
    // doy para imprimir a varias salen todas pegadas").
    //
    // No merece la pena seguir averiguando por qué esa hoja no se aplicaba: un
    // estilo escrito en el propio elemento no puede quedarse sin aplicar. Sale
    // un HTML más verboso a cambio de que la hoja no pueda descuadrarse.
    //
    // Y todo en PULGADAS: al imprimir, una pulgada es una pulgada. En píxeles,
    // cualquier cambio de zoom o de DPI sacaría las direcciones del adhesivo.
    const estiloCelda = [
      `width:${F.ancho}`,
      `height:${F.alto}`,
      "box-sizing:border-box",
      "overflow:hidden",
      "padding:0.08in 0.16in",
      "display:flex",
      "flex-direction:column",
      // Centrado vertical: pegado al borde superior, cualquier desalineación
      // de la impresora saca el texto fuera del adhesivo.
      "justify-content:center",
      "font-family:Arial,Helvetica,sans-serif",
      `font-size:${F.fuente}`,
      "line-height:1.18",
      "color:#000",
    ].join(";");
    const estiloLinea = "white-space:nowrap;overflow:hidden;text-overflow:ellipsis";
    const estiloNombre = `${estiloLinea};font-weight:bold`;

    const celdaVacia = `<div style="${estiloCelda}"></div>`;
    const celdas = [];
    for (let i = 0; i < saltar; i++) celdas.push(celdaVacia);
    for (const c of conDireccion) {
      const { calle, ultima } = lineasDireccion(c);
      celdas.push(
        `<div style="${estiloCelda}">` +
        `<div style="${estiloNombre}">${esc(nombreDe(c))}</div>` +
        `<div style="${estiloLinea}">${esc(calle)}</div>` +
        `<div style="${estiloLinea}">${esc(ultima)}</div>` +
        `</div>`,
      );
    }
    // Completar la última hoja para que la rejilla no quede coja.
    while (celdas.length % N !== 0) celdas.push(celdaVacia);

    const estiloHoja = [
      "display:grid",
      `grid-template-columns:repeat(${F.columnas},${F.ancho})`,
      `grid-auto-rows:${F.alto}`,
      `column-gap:${F.huecoColumna}`,
      "row-gap:0",
      "margin:0",
      "padding:0",
    ].join(";");

    // El documento que va a la impresora contiene SOLO las etiquetas.
    //
    // La versión anterior imprimía una página con una barra de botones arriba y
    // confiaba en `@media print { .barra { display:none } }` para quitarla. Eso
    // salió en el papel (2026-08-08: "solo quiero la label, no ese anuncio").
    // Confiar en una regla para ESCONDER algo del papel es frágil; que no
    // exista en el documento no puede fallar. Los avisos viven en el modal.
    //
    // El <style> que queda es solo para @page, que no se puede poner inline.
    // Si el navegador también lo ignorara, se perderían los márgenes de la
    // hoja pero la rejilla seguiría cuadrada.
    const html = `<!doctype html><html><head><meta charset="utf-8"><title></title>
<style>@page{size:letter;margin:${F.margenSuperior} ${F.margenLateral};}html,body{margin:0;padding:0;background:#fff;}</style>
</head><body><div style="${estiloHoja}">${celdas.join("")}</div></body></html>`;

    // Se imprime desde un iframe oculto de ESTA misma página, no desde una
    // ventana nueva. Dos motivos:
    //   - El bloqueador de ventanas emergentes ya no puede impedirlo.
    //   - Con window.open("") + document.write el documento a veces ya cuenta
    //     como cargado, y el disparo automático de impresión no llegaba nunca
    //     (por eso "no salía la impresora" el 2026-08-08).
    const marco = document.createElement("iframe");
    marco.setAttribute("aria-hidden", "true");
    marco.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(marco);

    const limpiar = () => { try { marco.remove(); } catch { /* ya se fue */ } };

    const doc = marco.contentWindow && marco.contentWindow.document;
    if (!doc) {
      limpiar();
      toast.error(es ? "No se pudo preparar la impresión." : "Couldn't prepare the print.");
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();

    // Un respiro para que el navegador termine de maquetar antes de fotografiar
    // la página para la impresora.
    setTimeout(() => {
      const v = marco.contentWindow;
      if (!v) { limpiar(); return; }
      // Quitar el iframe demasiado pronto cancela el trabajo en algunos
      // navegadores: se espera a afterprint, con una red de seguridad por si
      // ese evento no llega nunca.
      try { v.onafterprint = limpiar; } catch { /* opcional */ }
      setTimeout(limpiar, 60000);
      try {
        v.focus();
        v.print();
      } catch {
        limpiar();
        toast.error(es ? "El navegador bloqueó la impresión." : "The browser blocked printing.");
      }
    }, 300);
  };

  const N = porHoja(formato);
  const hojas = Math.ceil((conDireccion.length + Math.max(0, Number(empezarEn) - 1)) / N) || 1;

  return (
    <Modal onClose={onClose} title={es ? "Imprimir etiquetas de correo" : "Print mailing labels"} xl>
      <div style={{ display: "flex", flexDirection: "column", gap: ".85rem" }}>

        {/* Controles */}
        <div style={{ display: "flex", gap: ".9rem", flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
            <span style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--c-text2,#7aa3c0)" }}>
              {es ? "Cómo aparece el nombre" : "Name style"}
            </span>
            <select
              value={estilo}
              onChange={e => { setEstilo(e.target.value); setAjustes({}); }}
              style={{ padding: ".4rem .6rem", borderRadius: 7, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text,#c8dff0)", fontSize: ".8rem", fontFamily: "inherit", minWidth: 200 }}
            >
              {ESTILOS.map(o => <option key={o.id} value={o.id}>{es ? o.es : o.en}</option>)}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
            <span style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--c-text2,#7aa3c0)" }}>
              {es ? "Tamaño de etiqueta" : "Label size"}
            </span>
            <select
              value={formatoId}
              // Al cambiar de formato cambia cuántas caben por hoja, así que
              // "empezar en la nº 25" dejaría de tener sentido en una hoja de 14.
              onChange={e => { setFormatoId(e.target.value); setEmpezarEn(1); }}
              style={{ padding: ".4rem .6rem", borderRadius: 7, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text,#c8dff0)", fontSize: ".8rem", fontFamily: "inherit", minWidth: 250 }}
            >
              {FORMATOS.map(f => (
                <option key={f.id} value={f.id}>{f.nombre} — {es ? f.es : f.en}</option>
              ))}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
            <span style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--c-text2,#7aa3c0)" }}>
              {es ? "Empezar en la etiqueta nº" : "Start at label #"}
            </span>
            <input
              type="number" min={1} max={N} value={empezarEn}
              onChange={e => setEmpezarEn(e.target.value)}
              style={{ padding: ".4rem .6rem", borderRadius: 7, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text,#c8dff0)", fontSize: ".8rem", fontFamily: "inherit", width: 90 }}
            />
          </label>

          <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", paddingBottom: ".45rem" }}>
            {es
              ? `${N} por hoja · ${hojas} hoja${hojas === 1 ? "" : "s"}`
              : `${N} per sheet · ${hojas} sheet${hojas === 1 ? "" : "s"}`}
          </div>
        </div>

        <div style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)", lineHeight: 1.6 }}>
          {es
            ? "Si la hoja ya tiene etiquetas usadas, indica en cuál empezar y las primeras se dejan en blanco."
            : "If the sheet is partly used, set where to start and the first ones are left blank."}
          <br />
          {/* Estos dos ajustes son del navegador, no de Deskte, y son lo único
              que suele arruinar una plancha entera. Antes se avisaba dentro de
              la ventana de impresión; ahora esa ventana solo lleva etiquetas,
              así que el recordatorio vive aquí. */}
          <strong style={{ color: "var(--c-amber,#ffb700)" }}>
            {es ? "En el cuadro de impresión: " : "In the print dialog: "}
          </strong>
          {es
            ? 'escala 100% (no "Ajustar a la página"), y quita "Encabezados y pies de página" para que no salga la fecha impresa encima.'
            : 'set scale to 100% (not "Fit to page"), and turn off "Headers and footers" so the date isn\'t printed over the labels.'}
        </div>

        {/* Aviso de los que se quedan fuera. No se ocultan en silencio: si
            faltan direcciones hay que verlo antes de imprimir, no después.
            Solo tiene sentido una vez cargadas las fichas — antes, TODOS
            parecerían no tener dirección. */}
        {!cargando && sinDireccion.length > 0 && (() => {
          const fallidos = sinDireccion.filter(c => c.__noCargo);
          const vacios = sinDireccion.filter(c => !c.__noCargo);
          const nombres = (l) => l.slice(0, 6).map(c => `${c.firstName || ""} ${c.lastName || ""}`.trim()).join(", ")
            + (l.length > 6 ? (es ? ` y ${l.length - 6} más` : ` and ${l.length - 6} more`) : "");
          return (
            <div style={{ background: "rgba(255,183,0,.08)", border: "1px solid rgba(255,183,0,.3)", borderRadius: 8, padding: ".55rem .75rem", fontSize: ".74rem", color: "var(--c-text,#c8dff0)", display: "flex", flexDirection: "column", gap: ".3rem" }}>
              {vacios.length > 0 && (
                <div>
                  <strong style={{ color: "var(--c-amber,#ffb700)" }}>
                    {vacios.length} {es ? "sin dirección completa" : "without a full address"}
                  </strong>
                  {" — "}{es ? "no se imprimen: " : "not printed: "}{nombres(vacios)}
                </div>
              )}
              {/* Separado a propósito: "no tiene dirección" y "no pude
                  cargarla" se arreglan de formas distintas. */}
              {fallidos.length > 0 && (
                <div>
                  <strong style={{ color: "var(--c-red,#ff3d6b)" }}>
                    {fallidos.length} {es ? "no se pudieron cargar" : "failed to load"}
                  </strong>
                  {" — "}{nombres(fallidos)}
                  {". "}{es ? "Cierra y vuelve a abrir para reintentar." : "Close and reopen to retry."}
                </div>
              )}
            </div>
          );
        })()}

        {/* Vista previa editable */}
        <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8 }}>
          {cargando ? (
            <div style={{ padding: "1.2rem", textAlign: "center", fontSize: ".78rem", color: "var(--c-text3,#45688a)" }}>
              {es ? "Cargando direcciones" : "Loading addresses"}… {progreso}/{clients.length}
            </div>
          ) : conDireccion.length === 0 ? (
            <div style={{ padding: "1.2rem", textAlign: "center", fontSize: ".78rem", color: "var(--c-text3,#45688a)" }}>
              {es ? "Ninguno de los clientes seleccionados tiene dirección." : "None of the selected clients has an address."}
            </div>
          ) : conDireccion.map(c => {
            const { calle, ultima } = lineasDireccion(c);
            return (
              <div key={c.id} style={{ display: "flex", gap: ".6rem", alignItems: "center", padding: ".45rem .6rem", borderBottom: "1px solid var(--c-border,#1a3550)" }}>
                <input
                  value={nombreDe(c)}
                  onChange={e => setAjustes(prev => ({ ...prev, [c.id]: e.target.value }))}
                  title={es ? "Puedes corregir este nombre solo para esta impresión" : "You can adjust this name for this print only"}
                  style={{ flex: "0 0 240px", padding: ".3rem .5rem", borderRadius: 6, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface,#0d1c2e)", color: "var(--c-white,#eef6ff)", fontSize: ".76rem", fontWeight: 700, fontFamily: "inherit" }}
                />
                <div style={{ flex: 1, minWidth: 0, fontSize: ".72rem", color: "var(--c-text2,#7aa3c0)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {calle} · {ultima}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem" }}>
          <button type="button" onClick={onClose}
            style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text,#c8dff0)", fontWeight: 700, fontSize: ".8rem", cursor: "pointer", fontFamily: "inherit" }}>
            {es ? "Cancelar" : "Cancel"}
          </button>
          <button type="button" onClick={imprimir} disabled={cargando || conDireccion.length === 0}
            style={{ padding: ".5rem 1.2rem", borderRadius: 8, border: "none", background: (cargando || conDireccion.length === 0) ? "#155e75" : "var(--c-cyan,#00d4ff)", color: "#04121f", fontWeight: 800, fontSize: ".8rem", cursor: cargando ? "wait" : conDireccion.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {cargando
              ? (es ? "Cargando…" : "Loading…")
              : (es ? `Imprimir ${conDireccion.length}` : `Print ${conDireccion.length}`)}
          </button>
        </div>
      </div>
    </Modal>
  );
}
